import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Divider,
} from '@mui/material';
import { SupportService } from "../services/SupportService";
import {Helmet} from "react-helmet";

function Support() {
  // State for role selection ("Providers" or "Clients")
  const [selectedRole, setSelectedRole] = useState('');
  // State for a single selected issue
  const [selectedIssue, setSelectedIssue] = useState('');
  // Error messages for role and issue selection
  const [roleError, setRoleError] = useState('');
  const [issueError, setIssueError] = useState('');
  // Email address state (pre-populated from query parameter if provided)
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  // Additional info text and word count
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [wordCount, setWordCount] = useState(0);
  const [additionalInfoError, setAdditionalInfoError] = useState(false);
  // State to show confirmation message after sending
  const [messageSent, setMessageSent] = useState(false);

  // Regex for basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // On component mount, read query param "emailAddress"
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('emailAddress') || '';
    setEmail(emailParam);
    if (emailParam && !emailRegex.test(emailParam)) {
      setEmailError('Please enter a valid email address.');
    }
  }, []);

  // Handler for role button clicks
  const handleRoleSelection = (role) => {
    setSelectedRole(role);
    if (role) setRoleError('');
  };

  // Handler for issue button selection (only one can be selected)
  const handleIssueSelection = (issue) => {
    setSelectedIssue(issue);
    if (issue) setIssueError('');
  };

  // Handler for email change with validation
  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    if (newEmail && !emailRegex.test(newEmail)) {
      setEmailError('Please enter a valid email address.');
    } else {
      setEmailError('');
    }
  };

  // Handler for additional information text area, limiting to 100 words
  const handleAdditionalInfoChange = (event) => {
    const text = event.target.value;
    const words = text.trim().split(/\s+/).filter((word) => word !== '');
    if (words.length <= 100) {
      setAdditionalInfo(text);
      setWordCount(words.length);
      setAdditionalInfoError(false);
    } else {
      setAdditionalInfoError(true);
    }
  };

  const handleSend = async () => {
    let valid = true;

    if (!selectedRole) {
      setRoleError("Please select your role.");
      valid = false;
    } else {
      setRoleError("");
    }

    if (!selectedIssue) {
      setIssueError("Please select an issue.");
      valid = false;
    } else {
      setIssueError("");
    }

    if (!email || !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    }

    if (additionalInfoError) {
      valid = false;
    }

    if (!valid) return;

    try {
      const response = await SupportService.submitSupportTicket({
        role: selectedRole,
        issue: selectedIssue.replace(" Issue", ""), // normalize e.g., "Payment Issue" → "Payment"
        email,
        title: `${selectedIssue} from ${selectedRole}`,
        additionalInfo,
        priority: "Normal", // or map to priority dynamically if needed
      });

      if (response.status === 201) {
        setMessageSent(true);
      } else {
        console.error("API error:", response);
        alert("There was a problem submitting your request.");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      alert("There was a problem connecting to the support service.");
    }
  };


  // Array of issues (labels as desired)
  const issues = [
    "General Issue",
    "Payment Issue",
    "Scheduling Issue",
    "Technical Issue",
    "Illegal Activities",
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 2,
        marginBottom: '50px', // 50px page margin at the bottom
      }}
    >
      <Helmet>
        <title>PairCare - Support Center</title>
      </Helmet>
      {/* Page Heading */}
      <Typography variant="h4" align="center" gutterBottom>
        PairCare Support Center
      </Typography>
      {/* Light-gray divider below the heading */}
      <Divider sx={{ bgcolor: 'lightgray', width: '100%', marginBottom: 2 }} />

      {/* Role Selection Buttons */}
      <Typography variant="h6" align="center" gutterBottom>
        Please Select Your Role
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
        <Button
          variant={selectedRole === 'Providers' ? 'contained' : 'outlined'}
          onClick={() => handleRoleSelection('Providers')}
        >
          Providers
        </Button>
        <Button
          variant={selectedRole === 'Clients' ? 'contained' : 'outlined'}
          onClick={() => handleRoleSelection('Clients')}
        >
          Clients
        </Button>
      </Box>
      {roleError && (
        <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
          {roleError}
        </Typography>
      )}

      {/* Issue Selection Buttons */}
      <Typography variant="h6" align="center" gutterBottom>
        Select the Issues You're Experiencing
      </Typography>
      

      <Box
  sx={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 2,
    maxWidth: { xs: '100%', sm: 600, md: 900 },
    width: '100%',        
    margin: '0 auto',      // Centers the grid container
    marginBottom: 1,
  }}
>
  {issues.map((issue) => (
    <Button
      key={issue}
      variant={selectedIssue === issue ? 'contained' : 'outlined'}
      onClick={() => handleIssueSelection(issue)}
      sx={{
        width: '100%',       // Make each button take up the full grid cell width
        whiteSpace: 'nowrap', // Prevent text wrapping
      }}
    >
      {issue}
    </Button>
  ))}
</Box>


      {issueError && (
        <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
          {issueError}
        </Typography>
      )}

      {/* Email Address Input */}
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 600, md: 900 }, marginTop: 2 }}>
        <TextField
          fullWidth
          label="Your Email Address"
          variant="outlined"
          value={email}
          onChange={handleEmailChange}
          margin="normal"
          error={!!emailError}
          helperText={emailError}
        />
      </Box>

      {/* Additional Information Dialogue Box */}
      <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: 600, md: 900 }, marginTop: 2 }}>
        <TextField
          fullWidth
          label="Additional Information (max 100 words)"
          variant="outlined"
          value={additionalInfo}
          onChange={handleAdditionalInfoChange}
          multiline
          minRows={4}
          error={additionalInfoError}
          helperText={
            additionalInfoError
              ? 'Maximum 100 words allowed.'
              : `${wordCount} / 100 words`
          }
        />
      </Box>

      {/* Send Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleSend}
        disabled={!email || !!emailError || additionalInfoError}
        sx={{ marginTop: 2 }}
      >
        Send
      </Button>

      {/* Confirmation Message */}
      {messageSent && (
        <Typography variant="body1" align="center" sx={{ marginTop: 2 }}>
          Your message was sent. Our help center will contact you shortly.
        </Typography>
      )}
    </Box>
  );
}

export default Support;
